import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Image, Modal, Platform, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	BottomButtons,
	BtnCloseModal,
	LightBtn,
	ProfileChipPng,
	SmallVipSix,
} from '../../../assets';
import i18n from '../../../i18n/i18n';
import { cleverTapService } from '../../../services/clevertap/clevertap.service';
import { deltaDNAService } from '../../../services/deltaDNA/deltadna.service';
import {
	donationCurrency,
	donationErrorCode,
	donationProductValue,
	donationTypeOfPackage,
	donationTypeOfTab,
	getAnalyticsPlatform,
	trackDonationEvent,
} from '../../../services/donationAnalytics';
import { DonationContext, notifyDonationCompleted } from '../../../services/donationsApi';
import { analyticsEvents } from '../../../services/firebase/analytics-events.service';
import { RootState } from '../../../store';
import { updateUserVipStatus } from '../../../store/auth/authActions';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { USER_CANCELLED_PURCHASE } from '../../../store/iap/iapActions';
import {
	packagesRequest,
	paymentRequest,
	paymentReset,
} from '../../../store/packages/packagesActions';
import { Package, SUPPORTED_MODAL_ORIENTATIONS } from '../../../store/types';
import { env } from '../../../utils/env';
import { usePaymentStatus } from '../../../utils/hooks/paymentStatus';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useIAP } from '../../../utils/hooks/useIAP';
import { openWebPaymentWindow } from '../../../utils/webPaymentWindow';
import { RootStackParamList } from '../../navigator';
import { useFooterTop } from '../../Pages/footerHeight';
import AnimatedLoader from '../common/AnimatedLoader';
import { IndicatorScrollView } from '../common/ScrollIndicator';
import { TouchableOpacity } from '../common/Touchable';
import { ButtonCard } from '../Profile/ButtonCard';
import ChipScreen from '../Profile/BuyChips/ProfileChipScreen';
import VipScreen from '../Profile/BuyVip/ProfileVipScreen';
import ProfileTournaments from '../Profile/Tournaments/ProfileTournaments';
import StoreRedesignModal from '../Store/StoreRedesignModal';
import StripeLinkButton from '../Store/StripeLinkButton';
import DonationConfirmModal from './DonationConfirmModal';
import PaymentCheckoutModal, { PaymentProvider } from './PaymentCheckoutModal';
import { PaymentStatusModal } from './PaymentStatusModal';

type BuyPackagesRouteProp = { params?: RootStackParamList['BuyPackages'] };
type ShopTab = 'chips' | 'vip' | 'tournaments';

/** The design's gap between the end of the VIP page (its price panel) and the nav. */
const VIP_PAGE_NAV_GAP = 8;
type Nav = NativeStackNavigationProp<RootStackParamList>;

interface BuyPackagesModalProps {
	initialActiveTab?: ShopTab;
	initialPlayerId?: string;
	onClose: () => void;
	route?: BuyPackagesRouteProp;
	renderInline?: boolean;
	renderVipPage?: boolean;
}

interface NavigationTabs {
	tab: Extract<ShopTab, 'chips' | 'vip'>;
	labelKey: 'T_SITE_BUY_VIP' | 'T_SITE_PROFILE_BUY';
	icon?: any;
}

function BuyPackagesModal({
	initialActiveTab = 'chips',
	initialPlayerId,
	onClose,
	route,
	renderInline = false,
	renderVipPage = false,
}: BuyPackagesModalProps) {
	const hasTrackedRef = useRef(false);
	const nativePurchaseLockRef = useRef(false);
	const nativePurchaseAttemptRef = useRef(0);
	const currentLanguage = i18n.language || 'bg';
	const { t } = useTranslation();
	const navigation = useNavigation<Nav>();
	const { navigate } = navigation;
	const { insets, isDesktop, isWeb, isNative, isMobile } = useDevice();
	const footerTop = useFooterTop();
	// Where the VIP page's scroll area ends on screen, to finish its content just above the nav.
	const vipPageRef = useRef<View>(null);
	const [vipPageBottom, setVipPageBottom] = useState<number | null>(null);
	const isLogged = useSelector(selectIsLogged);
	const dispatch = useDispatch();
	const packageState = useSelector((state: RootState) => state.package);
	const paymentData = usePaymentStatus();
	const iap = useIAP();
	const [selectedProvider, setSelectedProvider] = useState<PaymentProvider>('stripe');
	const [showProviderModal, setShowProviderModal] = useState(false);
	const [pendingPackage, setPendingPackage] = useState<Package | null>(null);
	const [, setPurchasedPackage] = useState<Package | null>(null);

	const [nativePaymentPackage, setNativePaymentPackage] = useState<Package | null>(null);

	// The DONATION STORE: everything bought here is paid by the player and credited to `recipient`.
	const recipient = route?.params?.recipient;
	const isDonation = recipient != null;
	/** The confirmation step a donation takes before paying; `provider` is null for in-app. */
	const [donationConfirm, setDonationConfirm] = useState<{
		pkg: Package;
		provider: PaymentProvider | null;
	} | null>(null);
	/** A donation the in-app purchase delivered, for the success popup over the store. */
	const [donationResult, setDonationResult] = useState<{
		pkg: Package;
		donation: DonationContext;
	} | null>(null);
	const senderId = useSelector((state: RootState) =>
		state.auth?.user?.id != null ? String(state.auth.user.id) : '',
	);

	/** The donation whose checkout started last, for `donation_payment_failed`. */
	const donationCheckoutPackageRef = useRef<Package | null>(null);

	/** `donation_payment_failed` for the package being paid, when this store is a donation. */
	const trackDonationPaymentFailed = useCallback(
		(error: string | null | undefined) => {
			if (!recipient) return;

			const pkg = donationCheckoutPackageRef.current;
			trackDonationEvent('donation_payment_failed', {
				donation_type: pkg
					? donationTypeOfPackage(pkg)
					: donationTypeOfTab(route?.params?.activeTab ?? initialActiveTab),
				product_id: pkg?.id ?? '',
				error_code: donationErrorCode(error),
			});
		},
		[initialActiveTab, recipient, route?.params?.activeTab],
	);

	// Keep the purchase loader visible for at least MIN_PURCHASE_LOADER_MS so a fast
	// deposit-creation round trip never makes it flash imperceptibly.
	const MIN_PURCHASE_LOADER_MS = 600;
	const purchaseLoaderStartRef = useRef(0);
	const [purchaseLoaderVisible, setPurchaseLoaderVisible] = useState(false);

	useEffect(() => {
		if (packageState.paymentLoading || iap.verifyLoading) {
			purchaseLoaderStartRef.current = Date.now();
			setPurchaseLoaderVisible(true);
		}
	}, [packageState.paymentLoading, iap.verifyLoading]);

	useEffect(() => {
		if (!purchaseLoaderVisible) return;
		if (packageState.paymentLoading || iap.verifyLoading) return;

		const remaining = MIN_PURCHASE_LOADER_MS - (Date.now() - purchaseLoaderStartRef.current);
		if (remaining <= 0) {
			setPurchaseLoaderVisible(false);
			return;
		}
		const timer = setTimeout(() => setPurchaseLoaderVisible(false), remaining);
		return () => clearTimeout(timer);
	}, [purchaseLoaderVisible, packageState.paymentLoading, iap.verifyLoading]);

	const nativeModalSafeAreaInsets = isNative
		? {
				paddingTop: Math.max(12, insets.top),
				paddingBottom: Math.max(12, insets.bottom),
				paddingLeft: Math.max(12, insets.left),
				paddingRight: Math.max(12, insets.right),
			}
		: null;
	const { items, paymentError } = packageState;
	const isEasyPay = isWeb && selectedProvider === 'easypay' && showProviderModal;
	const showPurchaseLoader = purchaseLoaderVisible;

	const getUserData = () => {
		if (isWeb) {
			return JSON.parse(localStorage.getItem('auth_user') || '{}');
		}
		return {};
	};

	const user = getUserData();

	const handlePaymentComplete = useCallback(
		(pkg: Package, status: 'success' | 'cancelled' | 'error') => {
			if (status !== 'success') return;

			// A donated VIP membership is the recipient's, never the buyer's.
			if (pkg.type === 'VIP_MEMBERSHIP' && !isDonation) {
				dispatch(updateUserVipStatus({ isVip: true }));
			}

			cleverTapService.trackEvent(`${pkg.type} Purchased`, {
				package_id: pkg.id,
				price: pkg.price,
				currency: (pkg as any)?.currency,
			});
		},
		[dispatch, isDonation],
	);

	useEffect(() => {
		dispatch(paymentReset());
	}, [dispatch]);

	const donationStoreTrackedRef = useRef(false);
	useEffect(() => {
		// Once the buyer's own profile has loaded, so the event carries both ids.
		if (!recipient || !senderId || donationStoreTrackedRef.current) return;

		donationStoreTrackedRef.current = true;
		trackDonationEvent('donation_store_opened', {
			sender_id: senderId,
			recipient_id: recipient.playerId,
			donation_type: donationTypeOfTab(route?.params?.activeTab ?? initialActiveTab),
			platform: getAnalyticsPlatform(),
		});
	}, [initialActiveTab, recipient, route?.params?.activeTab, senderId]);

	useEffect(() => {
		if (hasTrackedRef.current) return;

		hasTrackedRef.current = true;
		analyticsEvents.trackOpenShop();
		cleverTapService.trackEvent('Opened shop', { testShop: 'test' });
	}, []);

	useEffect(() => {
		if (hasTrackedRef.current) {
			console.log('⏭️ Already tracked, skipping...');
			return;
		}

		const trackShopOpen = async () => {
			hasTrackedRef.current = true;
			console.log('opening shop');
			analyticsEvents.trackOpenShop();
			cleverTapService.trackEvent('Opened shop', {
				testShop: 'test',
			});

			await deltaDNAService.openShopEvent(
				Platform.OS,
				currentLanguage,
				user.level,
				0,
				user.chips,
				'regular',
				'chips',
				1,
			);
		};

		trackShopOpen();
	}, [currentLanguage, user]);

	useEffect(() => {
		if (paymentError) {
			trackDonationPaymentFailed(paymentError);
			Alert.alert(
				t('T_ERROR', { defaultValue: 'Error' }),
				t(paymentError, { defaultValue: paymentError }),
				[{ text: t('T_OK', { defaultValue: 'OK' }) }],
			);
		}
	}, [paymentError, t, trackDonationPaymentFailed]);

	useEffect(() => {
		dispatch(packagesRequest({ start: 0, limit: 20 }));
	}, [dispatch]);

	useEffect(() => {
		if (isWeb || items.length === 0) {
			return;
		}

		const iosPackages = items
			.filter(item => item.type === 'CHIPS' || item.type === 'VIP_MEMBERSHIP')
			.map(item => ({
				packageId: item.id,
				packageName: item.name,
				packageType: item.type,
				price: item.price,
				appleStoreProductId: item.metadata?.appleStore?.productId ?? null,
			}));

		console.log('[BuyPackages] Loaded iOS package metadata summary:', iosPackages);

		const duplicateAppleStoreProductIds = iosPackages.reduce(
			(acc, item) => {
				if (!item.appleStoreProductId) {
					return acc;
				}

				acc[item.appleStoreProductId] = acc[item.appleStoreProductId] || [];
				acc[item.appleStoreProductId].push({
					packageId: item.packageId,
					packageName: item.packageName,
					packageType: item.packageType,
					price: item.price,
				});
				return acc;
			},
			{} as Record<
				string,
				Array<{
					packageId: string;
					packageName: string;
					packageType: string;
					price: string | number | null | undefined;
				}>
			>,
		);

		const duplicates = Object.entries(duplicateAppleStoreProductIds).filter(
			([, mappedPackages]) => mappedPackages.length > 1,
		);

		if (duplicates.length > 0) {
			console.error(
				'[BuyPackages] Duplicate App Store product IDs detected across packages:',
				duplicates.map(([appleStoreProductId, mappedPackages]) => ({
					appleStoreProductId,
					mappedPackages,
				})),
			);
		}
	}, [isWeb, items]);

	useEffect(() => {
		if (isWeb) return;

		if (!iap.purchaseVerified || !nativePaymentPackage) {
			return;
		}

		handlePaymentComplete(nativePaymentPackage, 'success');

		if (recipient) {
			// Delivered to the recipient - the answer carries no `newBalance` for the buyer. The
			// popup over the store names them and opens the chat the donation just unlocked.
			notifyDonationCompleted(recipient.playerId);
			setDonationResult({
				pkg: nativePaymentPackage,
				donation: {
					recipientPlayerId: recipient.playerId,
					recipientUsername: recipient.username,
					type: nativePaymentPackage.type === 'VIP_MEMBERSHIP' ? 'VIP' : 'CHIPS',
					chipAmount: nativePaymentPackage.chipAmount ?? nativePaymentPackage.chips,
					vipDurationDays: nativePaymentPackage.vipDurationDays,
				},
			});
		} else if (nativePaymentPackage.type === 'VIP_MEMBERSHIP') {
			Alert.alert(
				t('T_SUCCESS', { defaultValue: 'Success' }),
				t('T_VIP_PURCHASE_SUCCESS', { defaultValue: 'VIP membership activated successfully!' }),
				[
					{
						text: t('T_OK', { defaultValue: 'OK' }),
						onPress: () => onClose(),
					},
				],
			);
		} else if (nativePaymentPackage.type === 'CHIPS') {
			Alert.alert(
				t('T_SUCCESS', { defaultValue: 'Success' }),
				t('T_CHIPS_PURCHASE_SUCCESS', { defaultValue: 'Chips purchased successfully!' }),
				[
					{
						text: t('T_OK', { defaultValue: 'OK' }),
						onPress: () => onClose(),
					},
				],
			);
		}

		nativePurchaseLockRef.current = false;
		iap.resetIAP();
		setNativePaymentPackage(null);
	}, [handlePaymentComplete, iap, isWeb, nativePaymentPackage, onClose, recipient, t]);

	useEffect(() => {
		if (isWeb || !iap.verifyError) {
			return;
		}

		trackDonationPaymentFailed(iap.verifyError);

		Alert.alert(
			t('T_ERROR', { defaultValue: 'Error' }),
			t(iap.verifyError, { defaultValue: iap.verifyError }),
			[{ text: t('T_OK', { defaultValue: 'OK' }) }],
		);
		nativePurchaseLockRef.current = false;
		iap.resetIAP();
		setNativePaymentPackage(null);
	}, [iap, iap.verifyError, isWeb, t, trackDonationPaymentFailed]);

	useEffect(() => {
		if (isWeb || !iap.purchaseError) {
			return;
		}

		// Backing out of the store's purchase sheet just returns to the shop.
		if (iap.purchaseError !== USER_CANCELLED_PURCHASE) {
			trackDonationPaymentFailed(iap.purchaseError);
			Alert.alert(
				t('T_ERROR', { defaultValue: 'Error' }),
				t(iap.purchaseError, { defaultValue: iap.purchaseError }),
				[{ text: t('T_OK', { defaultValue: 'OK' }) }],
			);
		}
		nativePurchaseLockRef.current = false;
		iap.resetIAP();
		setNativePaymentPackage(null);
	}, [iap, iap.purchaseError, isWeb, t, trackDonationPaymentFailed]);

	useEffect(() => {
		console.log('[BuyPackages] Payment data:', paymentData);

		if (isWeb || paymentData.status !== 'success' || !paymentData.packageInfo) {
			return;
		}

		console.log('Payment successful for package:', paymentData.packageInfo);

		const pkg = paymentData.packageInfo;

		let vipDurationDays: number | null = null;
		let offerType: number;

		if (pkg.type === 'VIP_MEMBERSHIP') {
			if (pkg.price === 6.49) {
				vipDurationDays = 30;
			} else if (pkg.price === 19.99) {
				vipDurationDays = 180;
			} else {
				vipDurationDays = 365;
			}
			offerType = 1;
		} else {
			offerType = 2;
		}

		cleverTapService.trackEvent(`${pkg.type} Purchased`, {
			package_id: pkg.id,
			price: pkg.price,
			currency: pkg.currency,
		});

		deltaDNAService.playerPaymentEvent(
			Platform.OS,
			user.chips,
			currentLanguage,
			Array.isArray(pkg.currency) ? pkg.currency[0] : pkg.currency,
			pkg.price || 0,
			selectedProvider,
			pkg.id || '',
			pkg.id || '',
			pkg.name || '',
			vipDurationDays || pkg.chipAmount || 0,
			offerType,
		);

		// A donation's result is the recipient's: no VIP for the buyer, no "chips purchased".
		if (isDonation) return;

		if (pkg.type === 'VIP_MEMBERSHIP') {
			dispatch(updateUserVipStatus({ isVip: true }));

			Alert.alert(
				t('T_SUCCESS', { defaultValue: 'Success' }),
				t('T_VIP_PURCHASE_SUCCESS', { defaultValue: 'VIP membership activated successfully!' }),
				[
					{
						text: t('T_OK', { defaultValue: 'OK' }),
						onPress: () => onClose(),
					},
				],
			);
		} else if (pkg.type === 'CHIPS') {
			Alert.alert(
				t('T_SUCCESS', { defaultValue: 'Success' }),
				t('T_CHIPS_PURCHASE_SUCCESS', { defaultValue: 'Chips purchased successfully!' }),
				[
					{
						text: t('T_OK', { defaultValue: 'OK' }),
					},
				],
			);
		}
	}, [
		currentLanguage,
		selectedProvider,
		user,
		paymentData,
		dispatch,
		isDonation,
		isWeb,
		t,
		onClose,
	]);

	useEffect(() => {
		// An EasyPay code is shown in the checkout sheet; the shop stays open under it.
		if (!isWeb || !packageState.paymentSuccess || isEasyPay) {
			return;
		}

		// The checkout runs in a separate popup tab on web - close the shop modal
		// here and let the app-level PaymentStatusModal confirm the result. Wait out
		// the minimum loader time so the loader is always visibly shown first.
		const remaining = MIN_PURCHASE_LOADER_MS - (Date.now() - purchaseLoaderStartRef.current);
		if (remaining > 0) {
			const timer = setTimeout(onClose, remaining);
			return () => clearTimeout(timer);
		}
		onClose();
	}, [isWeb, packageState.paymentSuccess, isEasyPay, onClose]);

	useEffect(() => {
		if (isWeb) return;
		if (!paymentData.status) return;

		if (paymentData.status === 'success' && paymentData.packageInfo) {
			handlePaymentComplete(paymentData.packageInfo as Package, 'success');
		} else if (paymentData.status === 'cancelled') {
			Alert.alert(
				t('T_CANCELLED', { defaultValue: 'Cancelled' }),
				t('T_PAYMENT_CANCELLED', { defaultValue: 'Payment was cancelled.' }),
				[{ text: t('T_OK', { defaultValue: 'OK' }) }],
			);
		} else {
			Alert.alert(
				t('T_ERROR', { defaultValue: 'Error' }),
				t('T_PAYMENT_FAILED', { defaultValue: 'Payment failed. Please try again.' }),
				[{ text: t('T_OK', { defaultValue: 'OK' }) }],
			);
		}
	}, [paymentData, t, handlePaymentComplete, isWeb]);

	// -------------------------
	// Helpers
	// -------------------------
	const getFrontendUrl = (): string => {
		const configuredFrontendUrl = env('SVARA_WEB_URL')!;

		if (Platform.OS !== 'web') {
			return configuredFrontendUrl;
		}

		try {
			const w = window as any;
			const origin = w?.location && typeof w.location.origin === 'string' ? w.location.origin : '';
			return origin || configuredFrontendUrl;
		} catch {
			return configuredFrontendUrl;
		}
	};

	const frontendUrl = getFrontendUrl();

	// -------------------------
	// Payment logic (unchanged)
	// -------------------------
	const handlePayment = (pkg: Package, provider: PaymentProvider = selectedProvider) => {
		setPurchasedPackage(pkg);
		setNativePaymentPackage(pkg);
		analyticsEvents.trackBeginCheckout(pkg.id);

		// EasyPay has no checkout page - the player gets a code to pay at an office - so it needs no tab.
		if (isWeb && provider !== 'easypay') {
			// Open the checkout tab synchronously within the user gesture so it is not
			// blocked as a popup. The saga navigates it once the checkout URL is ready.
			openWebPaymentWindow();
		}

		dispatch(
			paymentRequest({
				amount: Number(pkg.price || 0),
				pkg,
				currency: (pkg as any)?.currency?.name || (pkg as any)?.currency || 'EUR',
				frontendUrl,
				provider,
				recipient,
			}),
		);
	};

	const startNativePurchase = (pkg: Package) => {
		if (iap.isSupported && iap.isInitialized) {
			nativePurchaseAttemptRef.current += 1;
			console.log('[BuyPackages] Starting native IAP purchase attempt', {
				attempt: nativePurchaseAttemptRef.current,
				packageId: pkg.id,
				packageName: pkg.name,
				packageType: pkg.type,
				price: pkg.price,
				appleStoreProductId: pkg.metadata?.appleStore?.productId,
			});
			nativePurchaseLockRef.current = true;
			setPurchasedPackage(pkg);
			setNativePaymentPackage(pkg);
			analyticsEvents.trackBeginCheckout(pkg.id);
			// A donation is chips only in the apps, credited to the recipient on verify.
			iap.purchasePackage(pkg, recipient?.playerId);
		} else {
			console.log('[BuyPackages] IAP not available, using web payment:', pkg);
			handlePayment(pkg);
		}
	};

	/** The confirmation step's DONATE: the backend has allowed it, so pay the way it shows. */
	const handleDonationConfirm = (pkg: Package) => {
		const provider = donationConfirm?.provider ?? null;
		setDonationConfirm(null);
		donationCheckoutPackageRef.current = pkg;
		trackDonationEvent('donation_checkout_started', {
			donation_type: donationTypeOfPackage(pkg),
			product_id: pkg.id,
			price: Number(pkg.price || 0),
			currency: donationCurrency(pkg),
			platform: getAnalyticsPlatform(),
		});

		if (!isWeb) {
			startNativePurchase(pkg);
			return;
		}

		if (provider === 'easypay') {
			// EasyPay answers with a code, which the checkout sheet reopens to show.
			setPendingPackage(pkg);
			setShowProviderModal(true);
		}
		// Still inside the tap, so the checkout tab `handlePayment` opens is not a blocked popup.
		handlePayment(pkg, provider ?? selectedProvider);
	};

	const handleBuyPress = (pkg: Package) => {
		if (!isWeb && nativePurchaseLockRef.current) {
			console.log(
				'[BuyPackages] Ignoring buy press because a native purchase is already in flight',
				{
					packageId: pkg.id,
					packageName: pkg.name,
				},
			);
			return;
		}

		if (!isLogged) {
			onClose();
			navigate('Login');
			return;
		}

		if (isDonation) {
			trackDonationEvent('donation_product_selected', {
				donation_type: donationTypeOfPackage(pkg),
				product_id: pkg.id,
				product_value: donationProductValue(pkg),
			});
		}

		if (isWeb) {
			// A donation picks its method here too, then confirms before paying.
			setPendingPackage(pkg);
			setShowProviderModal(true);
			return;
		}

		if (isDonation) {
			// Without the store's in-app purchase the apps fall back to the card checkout.
			setDonationConfirm({
				pkg,
				provider: iap.isSupported && iap.isInitialized ? null : 'stripe',
			});
			return;
		}

		startNativePurchase(pkg);
	};

	const handleProviderSelect = (provider: PaymentProvider) => {
		setSelectedProvider(provider);
		// A donation goes to its confirmation step with the chosen method instead of paying now.
		if (isDonation) {
			setShowProviderModal(false);
			if (pendingPackage) setDonationConfirm({ pkg: pendingPackage, provider });
			setPendingPackage(null);
			return;
		}
		// EasyPay answers with a payment code, which the checkout sheet stays open to show.
		if (provider === 'easypay') {
			if (pendingPackage) handlePayment(pendingPackage, provider);
			return;
		}
		setShowProviderModal(false);
		if (pendingPackage) handlePayment(pendingPackage, provider);
		setPendingPackage(null);
	};

	const handleProviderClose = () => {
		setShowProviderModal(false);
		setPendingPackage(null);
		if (isEasyPay) dispatch(paymentReset());
	};

	const [activeTab, setActiveTab] = useState<ShopTab>(route?.params?.activeTab || initialActiveTab);
	// The donation store has no tournaments tab: anything but VIP opens on chips.
	const storeTab = isDonation && activeTab !== 'vip' ? 'chips' : activeTab;
	const [playerId, setPlayerId] = useState<string | undefined>(
		route?.params?.playerId || initialPlayerId,
	);
	useEffect(() => {
		setActiveTab(route?.params?.activeTab || initialActiveTab);
		setPlayerId(route?.params?.playerId || initialPlayerId);
	}, [initialActiveTab, initialPlayerId, route?.params?.activeTab, route?.params?.playerId]);

	const showBottomTabs = activeTab !== 'tournaments';
	const useStoreRedesign = (isDonation || activeTab !== 'tournaments') && !renderVipPage;

	const chipPackages = useMemo(
		() =>
			items.filter(item => item.type === 'CHIPS').sort((a, b) => (a?.price || 0) - (b?.price || 0)),
		[items],
	);

	const vipPackages = useMemo(
		() =>
			items
				.filter(item => item.type === 'VIP_MEMBERSHIP')
				.sort((a, b) => (a?.price || 0) - (b?.price || 0))
				.map(item => ({ ...item, name: item.name.replace(/VIP/gi, '') })),
		[items],
	);

	const topTabs: Array<NavigationTabs> = [
		{
			tab: 'chips',
			labelKey: 'T_SITE_PROFILE_BUY',
			icon: ProfileChipPng,
		},
		{
			tab: 'vip',
			labelKey: 'T_SITE_BUY_VIP',
			icon: SmallVipSix,
		},
	];

	const shopContent = (
		<>
			{activeTab === 'chips' && (
				<ChipScreen chipPackages={chipPackages} onPayment={handleBuyPress} />
			)}

			{activeTab === 'vip' && <VipScreen vipPackages={vipPackages} onPayment={handleBuyPress} />}

			{activeTab === 'tournaments' && <ProfileTournaments playerId={playerId} />}

			{paymentError && (
				<View style={styles.errorBox}>
					<Text style={styles.errorText}>{t(paymentError, { defaultValue: paymentError })}</Text>
				</View>
			)}
		</>
	);

	// Figma "VIP / Mobile — VIP Club Subscription" (2583:1064) ends the page with the price panel 8
	// above the nav. The footer floats over the bottom of this scroll area on native and sits a
	// little below it on web, so the area reaches down to the footer's top edge and its content is
	// padded by however far the footer reaches into it, plus those 8.
	const footerOverlap =
		vipPageBottom != null && footerTop != null
			? vipPageBottom - footerTop
			: // No footer measured - the desktop layout's dock, whose Home notch rises into the page -
				// keeps the clearance the page always had.
				isNative
				? 110
				: 64;
	const vipPageBottomPadding = Math.max(0, footerOverlap) + VIP_PAGE_NAV_GAP;
	const vipPageReach = Math.min(0, footerOverlap);

	const modalContent = renderVipPage ? (
		<View
			ref={vipPageRef}
			style={styles.vipPageContainer}
			onLayout={() =>
				vipPageRef.current?.measureInWindow((_x, y, _w, h) => setVipPageBottom(y + h))
			}
		>
			{/* No gutter for the bar: the page is centred with its own 15 margins (Figma V3, 6202:33673),
			    and the bar runs in the right margin. */}
			<IndicatorScrollView
				reserveGutter={false}
				style={[styles.vipPageScroll, { marginBottom: vipPageReach }]}
				contentContainerStyle={[styles.vipPageContent, { paddingBottom: vipPageBottomPadding }]}
				showsVerticalScrollIndicator={false}
				nestedScrollEnabled
			>
				{shopContent}
			</IndicatorScrollView>

			{showPurchaseLoader && (
				<View style={styles.loaderOverlay}>
					<AnimatedLoader scale={1.1} />
				</View>
			)}
		</View>
	) : useStoreRedesign ? (
		<StoreRedesignModal
			chipPackages={chipPackages}
			vipPackages={vipPackages}
			initialTab={storeTab === 'vip' ? 'vip' : 'chips'}
			error={paymentError}
			loading={packageState.loading}
			onBuy={handleBuyPress}
			onClose={onClose}
			onGoToClub={() => {
				onClose();
				setTimeout(() => navigate('VipSubscription'), 0);
			}}
			renderInline={renderInline}
			donationRecipient={recipient}
		/>
	) : (
		<View
			style={[styles.modalOverlay, nativeModalSafeAreaInsets, { minWidth: isDesktop ? 800 : 300 }]}
		>
			{!showPurchaseLoader && (
				<TouchableOpacity activeOpacity={1} onPress={onClose} style={styles.backdropTapArea} />
			)}
			<View
				style={[
					styles.modalContainer,
					{ width: isMobile ? '90%' : 'auto' },
					{ height: isMobile ? '80%' : 'auto' },
				]}
			>
				<IndicatorScrollView
					style={styles.scrollContainer}
					contentContainerStyle={[styles.scrollContent, { paddingBottom: isNative ? 65 : 0 }]}
					showsVerticalScrollIndicator={false}
					nestedScrollEnabled
				>
					<View style={styles.contentBody}>{shopContent}</View>
				</IndicatorScrollView>

				<View style={styles.closeButtonContainer}>
					<TouchableOpacity onPress={onClose} style={styles.closeBtn}>
						<Image source={BtnCloseModal} style={styles.closeIcon} />
					</TouchableOpacity>
				</View>

				{showBottomTabs && (
					<View
						style={[
							styles.bottomButtons,
							isNative && {
								transform: [{ translateY: -25 }],
							},
						]}
					>
						{topTabs.map(tabConfig => (
							<View
								key={tabConfig.tab}
								style={{
									...(isMobile ? { flex: 1, minWidth: 0 } : { width: 196 }),
								}}
							>
								<ButtonCard
									activeImage={LightBtn}
									inactiveImage={BottomButtons}
									label={t(tabConfig.labelKey)}
									onPress={() => setActiveTab(tabConfig.tab)}
									active={activeTab === tabConfig.tab}
									icon={tabConfig.icon}
									testId={`cta-buy-packages-tab-${tabConfig.tab}`}
								/>
							</View>
						))}
					</View>
				)}

				{showPurchaseLoader && (
					<View style={styles.loaderOverlay}>
						<AnimatedLoader scale={1.1} />
					</View>
				)}
			</View>
		</View>
	);

	return (
		<>
			{useStoreRedesign ? (
				modalContent
			) : renderInline ? (
				modalContent
			) : (
				<Modal
					transparent
					visible
					statusBarTranslucent
					navigationBarTranslucent
					supportedOrientations={SUPPORTED_MODAL_ORIENTATIONS}
				>
					{modalContent}
				</Modal>
			)}

			{/* Web only: native buys go through the store's in-app purchase. */}
			{isWeb && (
				<PaymentCheckoutModal
					pkg={pendingPackage}
					visible={showProviderModal}
					easyPay={
						isEasyPay && packageState.payment?.paymentCode
							? {
									paymentCode: packageState.payment.paymentCode,
									transactionId: packageState.payment.transactionId ?? '',
								}
							: null
					}
					easyPayLoading={isEasyPay && packageState.paymentLoading}
					easyPayError={isEasyPay ? paymentError : undefined}
					onSelect={handleProviderSelect}
					onClose={handleProviderClose}
					// A donation confirms first, so its Link button goes on the confirmation instead.
					renderLinkButton={
						isWeb && !isDonation && pendingPackage
							? slot => (
									<StripeLinkButton
										pkg={pendingPackage}
										frontendUrl={frontendUrl}
										height={slot.height}
										fallback={slot.fallback}
										placeholder={slot.placeholder}
										onLoadingChange={slot.onLoadingChange}
									/>
								)
							: undefined
					}
				/>
			)}

			{recipient && donationConfirm ? (
				<DonationConfirmModal
					pkg={donationConfirm.pkg}
					recipient={recipient}
					provider={donationConfirm.provider}
					onConfirm={handleDonationConfirm}
					onClose={() => setDonationConfirm(null)}
					renderPayButton={
						isWeb && donationConfirm.provider === 'link'
							? (fallback, slotHeight) => (
									<StripeLinkButton
										pkg={donationConfirm.pkg}
										recipient={recipient}
										frontendUrl={frontendUrl}
										height={slotHeight}
										fallback={fallback}
									/>
								)
							: undefined
					}
				/>
			) : null}

			{/* In-app donations confirm here; web ones come back through the app-level popup. */}
			{donationResult ? (
				<PaymentStatusModal
					visible
					status='success'
					packageInfo={{
						type: donationResult.pkg.type,
						id: donationResult.pkg.id,
						name: donationResult.pkg.name,
						chipAmount: donationResult.pkg.chipAmount ?? donationResult.pkg.chips,
					}}
					donation={donationResult.donation}
					onClose={() => {
						setDonationResult(null);
						onClose();
					}}
				/>
			) : null}
		</>
	);
}

const styles = StyleSheet.create({
	modalOverlay: {
		flex: 1,
		backgroundColor: 'rgba(0,0,0,0.7)',
		justifyContent: 'center',
		alignItems: 'center',
	},
	bottomButtons: {
		flexDirection: 'row',
		justifyContent: 'center',
		width: '100%',
		marginTop: 6,
	},
	androidBottomButtons: {
		marginBottom: 40,
	},

	modalContainer: {
		maxHeight: '80%',
		minHeight: '40%',
		backgroundColor: '#112345',
		borderRadius: 10,
		borderWidth: 1,
		borderColor: '#00f0ff',
		position: 'relative',
		alignSelf: 'center',
		zIndex: 1,
	},
	backdropTapArea: {
		...StyleSheet.absoluteFill,
	},
	scrollContainer: {
		flex: 1,
		width: '100%',
		padding: 16,
	},
	scrollContent: {
		flexGrow: 1,
		justifyContent: 'center',
	},
	contentBody: {
		width: '100%',
		flexGrow: 1,
		justifyContent: 'center',
	},
	vipPageContainer: {
		flex: 1,
		width: '100%',
		minHeight: 0,
	},
	vipPageScroll: {
		flex: 1,
		width: '100%',
	},
	vipPageContent: {
		flexGrow: 1,
		alignItems: 'center',
		paddingTop: 4,
	},
	errorBox: {
		backgroundColor: '#331010',
		paddingVertical: 10,
		paddingHorizontal: 16,
		borderRadius: 20,
		alignSelf: 'center',
		marginTop: 15,
		marginBottom: 20,
	},
	errorText: {
		color: '#ff7b7b',
		fontSize: 13,
		fontWeight: '500',
		textAlign: 'center',
	},
	closeButtonContainer: {
		position: 'absolute',
		top: -24,
		right: -4,
		zIndex: 20,
	},
	closeBtn: {
		width: 50,
		height: 50,
	},
	closeIcon: {
		width: 50,
		height: 50,
	},
	loaderOverlay: {
		...StyleSheet.absoluteFill,
		backgroundColor: 'rgba(0,0,0,0.55)',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 30,
	},
});

export default BuyPackagesModal;
